import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Euro, FileText, Calendar, Info, X, Filter, Download, FileSpreadsheet } from 'lucide-react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AppointmentService from '../../services/appoinment.service';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { formatTime, formatDate } from '../../utils/dateUtils';

const AdminBillingPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allAppointments, setAllAppointments] = useState([]);
    const [billingData, setBillingData] = useState([]);
    const [doctorsList, setDoctorsList] = useState([]);

    // Filtri
    const [selectedDoctor, setSelectedDoctor] = useState('all');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState('all');

    // UI State
    const [expandedMonths, setExpandedMonths] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedMonthData, setSelectedMonthData] = useState(null);
    const [showExportOptions, setShowExportOptions] = useState(false);

    useEffect(() => {
        loadAdminBillingData();
    }, []);

    useEffect(() => {
        // Ricalcola i dati quando cambiano i filtri
        if (allAppointments.length > 0) {
            const filteredData = filterAndGenerateBillingData();
            setBillingData(filteredData);
        }
    }, [selectedDoctor, selectedYear, selectedMonth, allAppointments]);

    const loadAdminBillingData = async () => {
        try {
            setLoading(true);

            // Recupera tutti gli appuntamenti per admin
            const appointments = await AppointmentService.getAdminAppointments();

            // Filtra solo appuntamenti confirmed
            const confirmedAppointments = appointments.filter(app => app.status === 'confirmed');

            setAllAppointments(confirmedAppointments);

            // Estrai la lista di dottori unici
            const doctorsMap = new Map();
            confirmedAppointments.forEach(app => {
                if (!doctorsMap.has(app.therapist_id)) {
                    doctorsMap.set(app.therapist_id, {
                        id: app.therapist_id,
                        name: app.therapist_name || `Dottore #${app.therapist_id}`
                    });
                }
            });
            const uniqueDoctors = Array.from(doctorsMap.values());

            setDoctorsList(uniqueDoctors);

            // Genera i dati iniziali
            const billingStats = generateBillingData(confirmedAppointments);
            setBillingData(billingStats);

            setError(null);
        } catch (err) {
            console.error('Errore nel caricamento dei dati di fatturazione admin:', err);
            setError('Non è stato possibile caricare i dati di fatturazione. Riprova più tardi.');
        } finally {
            setLoading(false);
        }
    };

    const filterAndGenerateBillingData = () => {
        let filteredAppointments = [...allAppointments];

        // Filtro per dottore
        if (selectedDoctor !== 'all') {
            filteredAppointments = filteredAppointments.filter(app =>
                app.therapist_id.toString() === selectedDoctor
            );
        }

        // Filtro per anno
        filteredAppointments = filteredAppointments.filter(app => {
            if (!app.date) return false;
            const year = new Date(app.date).getFullYear();
            return year === selectedYear;
        });

        // Filtro per mese
        if (selectedMonth !== 'all') {
            filteredAppointments = filteredAppointments.filter(app => {
                if (!app.date) return false;
                const month = new Date(app.date).getMonth() + 1;
                return month === parseInt(selectedMonth);
            });
        }

        return generateBillingData(filteredAppointments);
    };

    const generateBillingData = (appointments) => {
        if (appointments.length === 0) return [];

        // Raggruppa per dottore e mese
        const appointmentsByDoctorAndMonth = {};

        appointments.forEach(appointment => {
            if (!appointment.date) return;

            const date = new Date(appointment.date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthName = date.toLocaleString('it-IT', { month: 'long', year: 'numeric' });
            const doctorId = appointment.therapist_id;
            const doctorName = appointment.therapist_name || `Dottore #${doctorId}`;
            const commissionRate = appointment.therapist_commission_rate || 75; // Default 75%

            const key = `${doctorId}-${monthKey}`;

            if (!appointmentsByDoctorAndMonth[key]) {
                appointmentsByDoctorAndMonth[key] = {
                    key: key,
                    doctorId: doctorId,
                    doctorName: doctorName,
                    monthKey: monthKey,
                    monthName: monthName,
                    appointments: [],
                    count: 0,
                    totalRevenue: 0,
                    commission: 0,
                    commissionRate: commissionRate,
                    sortDate: date
                };
            }

            //const price = parseFloat(appointment.price || 0);
            const price = parseFloat(appointment.actual_amount || 0);
            appointmentsByDoctorAndMonth[key].appointments.push(appointment);
            appointmentsByDoctorAndMonth[key].count += 1;
            appointmentsByDoctorAndMonth[key].totalRevenue += price;
            appointmentsByDoctorAndMonth[key].commission = appointmentsByDoctorAndMonth[key].totalRevenue * (commissionRate / 100);
        });

        // Converti in array e ordina
        return Object.values(appointmentsByDoctorAndMonth)
            .sort((a, b) => b.sortDate - a.sortDate);
    };

    // Funzione per esportare in CSV
    const exportToCSV = () => {
        const headers = ['Dottore', 'Periodo', 'N° Appuntamenti', 'Fatturato', '% Commissione', 'Compenso'];
        
        const csvRows = [
            headers.join(','),
            ...billingData.map(item => {
                return [
                    item.doctorName,
                    item.monthName,
                    item.count,
                    `€${item.totalRevenue.toFixed(2)}`,
                    `${item.commissionRate}%`,
                    `€${item.commission.toFixed(2)}`
                ].join(',');
            })
        ];
        
        // Aggiungi totali
        const totals = calculateTotals();
        csvRows.push(''); // Riga vuota
        csvRows.push('TOTALI GENERALI');
        csvRows.push(`Appuntamenti Totali,${totals.totalAppointments}`);
        csvRows.push(`Fatturato Totale,€${totals.totalRevenue.toFixed(2)}`);
        csvRows.push(`Compensi da Pagare,€${totals.totalCommission.toFixed(2)}`);
        
        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `fatturazione_${format(new Date(), 'yyyyMMdd')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setShowExportOptions(false);
    };

    // Funzione per esportare in Excel
    const exportToExcel = () => {
        // Crea un nuovo workbook
        const wb = XLSX.utils.book_new();
        
        // Foglio 1: Riepilogo
        const summaryHeaders = ['Dottore', 'Periodo', 'N° Appuntamenti', 'Fatturato', '% Commissione', 'Compenso'];
        const summaryData = [
            summaryHeaders,
            ...billingData.map(item => [
                item.doctorName,
                item.monthName,
                item.count,
                item.totalRevenue,
                item.commissionRate,
                item.commission
            ])
        ];
        
        // Aggiungi totali al riepilogo
        const totals = calculateTotals();
        summaryData.push([]); // Riga vuota
        summaryData.push(['TOTALI GENERALI']);
        summaryData.push(['Appuntamenti Totali', totals.totalAppointments]);
        summaryData.push(['Fatturato Totale', totals.totalRevenue]);
        summaryData.push(['Compensi da Pagare', totals.totalCommission]);
        
        const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, ws1, 'Riepilogo');
        
        // Foglio 2: Dettaglio Appuntamenti
        const detailHeaders = ['Dottore', 'Data', 'Orario', 'Paziente', 'Servizio', 'Tariffa', 'Compenso'];
        const detailData = [detailHeaders];
        
        billingData.forEach(item => {
            item.appointments.forEach(appointment => {
                const appointmentCommission = parseFloat(appointment.price || 0) * (item.commissionRate / 100);
                detailData.push([
                    item.doctorName,
                    format(new Date(appointment.date), 'dd/MM/yyyy', { locale: it }),
                    formatTime(appointment.time),
                    appointment.patient_name || `Paziente #${appointment.patient_id}`,
                    appointment.service_name,
                    parseFloat(appointment.actual_amount || 0),
                    appointmentCommission
                ]);
            });
        });
        
        const ws2 = XLSX.utils.aoa_to_sheet(detailData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Dettaglio Appuntamenti');
        
        // Configura larghezze colonne per entrambi i fogli
        const colWidthsSummary = [
            { wch: 25 }, // Dottore
            { wch: 20 }, // Periodo
            { wch: 15 }, // N° Appuntamenti
            { wch: 15 }, // Fatturato
            { wch: 15 }, // % Commissione
            { wch: 15 }, // Compenso
        ];
        ws1['!cols'] = colWidthsSummary;
        
        const colWidthsDetail = [
            { wch: 25 }, // Dottore
            { wch: 12 }, // Data
            { wch: 10 }, // Orario
            { wch: 20 }, // Paziente
            { wch: 25 }, // Servizio
            { wch: 12 }, // Tariffa
            { wch: 12 }, // Compenso
        ];
        ws2['!cols'] = colWidthsDetail;
        
        // Genera il file e avvia il download
        XLSX.writeFile(wb, `fatturazione_${format(new Date(), 'yyyyMMdd')}.xlsx`);
        setShowExportOptions(false);
    };

    const toggleMonthExpansion = (monthKey) => {
        setExpandedMonths(prev => ({
            ...prev,
            [monthKey]: !prev[monthKey]
        }));
    };

    const openMonthModal = (monthData) => {
        setSelectedMonthData(monthData);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMonthData(null);
    };

    const calculateTotals = () => {
        return billingData.reduce((totals, item) => ({
            totalAppointments: totals.totalAppointments + item.count,
            totalRevenue: totals.totalRevenue + item.totalRevenue,
            totalCommission: totals.totalCommission + item.commission
        }), { totalAppointments: 0, totalRevenue: 0, totalCommission: 0 });
    };

    const getAvailableYears = () => {
        const years = Array.from(new Set(allAppointments.map(app =>
            new Date(app.date).getFullYear()
        ))).sort((a, b) => b - a);
        return years;
    };

    const getAvailableMonths = () => {
        return [
            { value: 'all', label: 'Tutti i mesi' },
            { value: '1', label: 'Gennaio' },
            { value: '2', label: 'Febbraio' },
            { value: '3', label: 'Marzo' },
            { value: '4', label: 'Aprile' },
            { value: '5', label: 'Maggio' },
            { value: '6', label: 'Giugno' },
            { value: '7', label: 'Luglio' },
            { value: '8', label: 'Agosto' },
            { value: '9', label: 'Settembre' },
            { value: '10', label: 'Ottobre' },
            { value: '11', label: 'Novembre' },
            { value: '12', label: 'Dicembre' }
        ];
    };

    if (loading) {
        return (
            <div className="flex min-h-screen">
                <div className="w-64 bg-white shadow-md">
                    <AdminSidebar />
                </div>
                <div className="flex-1 bg-gray-50 p-8 flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600 mb-4"></div>
                        <p className="text-gray-600">Caricamento dati di fatturazione...</p>
                    </div>
                </div>
            </div>
        );
    }

    const totals = calculateTotals();

    return (
        <div className="flex min-h-screen">
            <div className="w-64 bg-white shadow-md">
                <AdminSidebar />
            </div>

            <div className="flex-1 bg-gray-50 p-8">
                <header className="mb-8">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold">Rendicontazione - Amministrazione</h1>
                            <p className="text-gray-600">Gestione compensi e fatturazione per dottori</p>
                        </div>
                        <div className="relative">
                            <button 
                                onClick={() => setShowExportOptions(!showExportOptions)}
                                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Esporta
                            </button>
                            
                            {showExportOptions && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                                    <div className="py-1">
                                        <button
                                            onClick={exportToCSV}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Esporta CSV
                                        </button>
                                        <button
                                            onClick={exportToExcel}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                                            Esporta Excel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
                        <p>{error}</p>
                    </div>
                )}

                {/* Filtri */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <div className="flex items-center mb-4">
                        <Filter className="w-5 h-5 text-gray-500 mr-2" />
                        <h2 className="text-lg font-semibold">Filtri</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Filtro Dottore */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Dottore
                            </label>
                            <select
                                value={selectedDoctor}
                                onChange={(e) => setSelectedDoctor(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">Tutti i dottori</option>
                                {doctorsList.map(doctor => (
                                    <option key={doctor.id} value={doctor.id}>
                                        {doctor.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Filtro Anno */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Anno
                            </label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                {/*getAvailableYears().map(year => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))*/}
                                <option key="2025" value="2025">2025</option>
                                <option key="2026" value="2026">2026</option>
                            </select>
                        </div>

                        {/* Filtro Mese */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mese
                            </label>
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                {getAvailableMonths().map(month => (
                                    <option key={month.value} value={month.value}>
                                        {month.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Riepilogo Totali */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-600">Appuntamenti Totali</p>
                                <p className="text-2xl font-bold">{totals.totalAppointments}</p>
                            </div>
                            <Calendar className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-600">Fatturato Totale</p>
                                <p className="text-2xl font-bold">€{totals.totalRevenue.toFixed(2)}</p>
                            </div>
                            <Euro className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-600">Compensi da Pagare</p>
                                <p className="text-2xl font-bold text-green-600">€{totals.totalCommission.toFixed(2)}</p>
                            </div>
                            <FileText className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>
                </div>

                {/* Tabella Risultati */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold">Dettaglio per Dottore e Periodo</h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Dottore
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Periodo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        N° Appuntamenti
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Fatturato
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        % Commissione
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Compenso
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Azioni
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {billingData.map((item) => (
                                    <React.Fragment key={item.key}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {item.doctorName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 capitalize">
                                                    {item.monthName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{item.count}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">€{item.totalRevenue.toFixed(2)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{item.commissionRate}%</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-green-600">
                                                    €{item.commission.toFixed(2)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex space-x-3">
                                                    <button
                                                        onClick={() => openMonthModal(item)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Visualizza dettagli appuntamenti"
                                                    >
                                                        <Info className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => toggleMonthExpansion(item.key)}
                                                        className="text-purple-600 hover:text-purple-900"
                                                        title="Espandi/Comprimi"
                                                    >
                                                        {expandedMonths[item.key] ? (
                                                            <ChevronUp className="w-5 h-5" />
                                                        ) : (
                                                            <ChevronDown className="w-5 h-5" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Riga espansa */}
                                        {expandedMonths[item.key] && (
                                            <tr>
                                                <td colSpan={7} className="px-6 py-4 bg-gray-50">
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                        <div className="text-center">
                                                            <p className="text-xs text-gray-500 uppercase">Appuntamenti</p>
                                                            <p className="text-lg font-semibold">{item.count}</p>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-xs text-gray-500 uppercase">Fatturato</p>
                                                            <p className="text-lg font-semibold">€{item.totalRevenue.toFixed(2)}</p>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-xs text-gray-500 uppercase">Compenso</p>
                                                            <p className="text-lg font-semibold text-green-600">€{item.commission.toFixed(2)}</p>
                                                        </div>
                                                        <div className="text-center">
                                                            <p className="text-xs text-gray-500 uppercase">Tariffa Media</p>
                                                            <p className="text-lg font-semibold">€{(item.totalRevenue / item.count).toFixed(2)}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {billingData.length === 0 && !loading && (
                    <div className="bg-white p-8 rounded-lg shadow text-center">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Nessun dato trovato
                        </h3>
                        <p className="text-gray-500">
                            Non ci sono appuntamenti per i filtri selezionati.
                        </p>
                    </div>
                )}
            </div>

            {/* Modale dettagli */}
            {showModal && selectedMonthData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-screen overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold">
                                {selectedMonthData.doctorName} - {selectedMonthData.monthName}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Riepilogo nella modale */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                            <div className="text-center">
                                <p className="text-xs text-gray-500 uppercase">Totale Appuntamenti</p>
                                <p className="text-2xl font-bold">{selectedMonthData.count}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-gray-500 uppercase">Fatturato</p>
                                <p className="text-2xl font-bold">€{selectedMonthData.totalRevenue.toFixed(2)}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-gray-500 uppercase">Compenso ({selectedMonthData.commissionRate}%)</p>
                                <p className="text-2xl font-bold text-green-600">€{selectedMonthData.commission.toFixed(2)}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-gray-500 uppercase">Tariffa Media</p>
                                <p className="text-2xl font-bold">€{(selectedMonthData.totalRevenue / selectedMonthData.count).toFixed(2)}</p>
                            </div>
                        </div>

                        {/* Tabella appuntamenti */}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orario</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paziente</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servizio</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tariffa</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Compenso</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {selectedMonthData.appointments
                                        .sort((a, b) => {
                                            const dateA = new Date(a.date + 'T' + a.time);
                                            const dateB = new Date(b.date + 'T' + b.time);
                                            return dateA.getTime() - dateB.getTime();
                                        })
                                        .map((appointment) => {
                                            //const appointmentPrice = parseFloat(appointment.price || 0);
                                            const appointmentPrice = parseFloat(appointment.actual_amount || 0);
                                            const appointmentCommission = appointmentPrice * (selectedMonthData.commissionRate / 100);

                                            return (
                                                <tr key={appointment.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {formatDate(appointment.date)}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {formatTime(appointment.time)}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {appointment.patient_name || `Paziente #${appointment.patient_id}`}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {appointment.service_name}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                        €{appointmentPrice.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-green-600">
                                                        €{appointmentCommission.toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBillingPage;